<template>
  <div>
    <div class="row page-titles">
      <div class="col-md-5 align-self-center">
        <h4 class="text-themecolor">{{ $t('stock') }}</h4>
      </div>
      <div class="col-md-7 align-self-center text-right">
        <div class="d-flex justify-content-end align-items-center">
          <breadcrumb :page="currentPage" />
        </div>
        <div class="last-update" v-if="!form.stockOOLicense">{{ $t('updated') }}: {{ item.last_sync |
          datetimeDisplay(defaultLocale) }}</div>
        <div class="economic-data" v-if="!form.stockOOLicense">{{ $t('economic_data') }}: <span
            class="economic-data-detail">{{ item.latest_report }}</span></div>
      </div>
    </div>

    <loading-container :loading="form.stockInfoLoading">
      <div v-if="form.hasData" class="row">
        <!-- Column -->
        <div class="col-lg-4 col-md-5" v-if="item.code">
          <div class="card">
            <div class="card-body">
              <h5 class="text-themecolor cursor-pointer m-b-0" @click="() => collapseSidebar('toggleInfo')">
                <i class="fas fa-qrcode"></i>
                <span :class="{ 'text-danger': item.warning }"> {{ $t('stocks.information') }}</span>
                <i v-if="item.warning" class="text-danger fas fa-exclamation-triangle m-l-5"></i>
                <change-status v-if="item.v_price" :status="item.v_price" :unit="1" />
                <a class="nav-link waves-effect waves-dark toggle-tab" data-toggle="dropdown">
                  <span>
                    <i class="fa" :class="{
                      'fa-angle-up': !this.form.toggleInfo,
                      'fa-angle-down': this.form.toggleInfo
                    }"></i>
                  </span>
                </a>
              </h5>
              <div v-if="!this.form.toggleInfo" class="mt-2 position-relative">
                <AddWatchList />
                <small class="text-muted">{{ $t('stocks.code') }}</small>
                <h6>
                  {{ item.code }}
                  <span class="font-12" v-if="item.vn30"> - <span class="text-warning bg-danger">VN30</span></span>
                  <span v-if="item.warning">
                    <span class="mytooltip tooltip-effect-5">
                      <i class="text-danger fas fa-exclamation-triangle m-l-5"></i>
                      <span class="tooltip-content clearfix">
                        <span class="tooltip-text p-t-10">Cổ phiếu đang trong trạng thái không ổn định</span>
                      </span>
                    </span>
                    <span class="m-l-5 text-danger">{{ item.warning_message }}</span>
                  </span>
                </h6>
                <small class="text-muted">{{ $t('stocks.name') }}</small>
                <h6>{{ item.name }}</h6>
                <small class="text-muted">{{ $t('stocks.description') }}</small>
                <h6>{{ formattedDescription() }}
                  <button class="btn btn-xs p-0" v-if="item.desc.length > 100"
                    @click="() => collapseSidebar('toggleDes')">
                    {{ form.toggleDes ? $t('common.view_less') : $t('common.view_more') }}
                  </button>
                </h6>
                <small class="text-muted">{{ $t('stocks.sector') }} - {{ $t('stocks.industry') }}</small>
                <h6>
                  {{ item.sector }} - {{ item.industry }}
                  <span class="cursor-pointer text-blue medium-icon" @click="() => actionGetSameIndustry('sameIndustry')">
                    <i class="fas fa-industry"></i>
                    <!-- <i class="mdi mdi-compare text-warning"></i> -->
                  </span>
                </h6>
                <small class="text-muted">{{ $t('stocks.type_label') }}</small>
                <h6>{{ $t(`stocks.type.` + item.type + `.tip`) }}</h6>
                <small class="text-muted">{{ $t('stocks.no_of_shares') }}</small>
                <h6>{{ item.shares | numberDisplay }}</h6>
                <div class="row two-col">
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.current_pb')" :msg="$t('stocks.pb.tip')" />
                    <h6><index-status :status="item.r_pb" :value="item.current_PB | numberDisplayFloat(3)" /></h6>
                  </div>
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.pb.title')" :msg="$t('stocks.pb.tip')" />
                    <h6>{{ item.pb | numberDisplayFloat(3) }}</h6>
                  </div>
                </div>
                <div class="row two-col">
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.current_eps')" :msg="$t('stocks.eps.tip')" />
                    <h6>{{ item.current_EPS | numberDisplay }} <span class="currencyFlag">{{ $t('currency') }}</span>
                    </h6>
                  </div>
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.eps.title')" :msg="$t('stocks.eps.tip')" />
                    <h6>{{ item.eps | numberDisplay }} <span class="currencyFlag">{{ $t('currency') }}</span></h6>
                  </div>
                </div>
                <div class="row two-col">
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.current_pe')" :msg="$t('stocks.pe.tip')" />
                    <h6><index-status :status="item.r_pe" :value="item.current_PE | numberDisplayFloat()" /></h6>
                  </div>
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.pe.title')" :msg="$t('stocks.pe.tip')" />
                    <h6>{{ item.pe | numberDisplayFloat() }}</h6>
                  </div>
                </div>
                <div class="row two-col">
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.pvgo.title')" :msg="$t('stocks.pvgo.tip')" />
                    <h6>{{ item.pvgo | numberDisplayFloat(0) }} <span class="currencyFlag">{{ $t('currency') }}</span>
                    </h6>
                  </div>
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.peg.title')" :msg="$t('stocks.peg.tip')" />
                    <h6><index-status :status="item.r_peg" :value="item.peg | numberDisplayFloat()" /></h6>
                  </div>
                </div>
                <div class="row two-col">
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.reinvestment_rate.title')" :msg="$t('stocks.reinvestment_rate.tip')" />
                    <h6>{{ item.reinvestment_rate | percentDisplay() }}</h6>
                  </div>
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.market_capital.title')" :msg="$t('stocks.market_capital.tip')" />
                    <h6><index-status :status="item.r_market_capital" :value="item.market_capital | numberDisplay" />
                    </h6>
                  </div>
                </div>
                <div class="row two-col">
                  <div class="col-sm-6 col-md-6">
                    <small class="text-muted">{{ $t('stocks.beta') }}</small>
                    <h6>{{ item.beta }}</h6>
                  </div>
                  <div class="col-sm-6 col-md-6">
                    <tooltip :isText="$t('stocks.has_treasury_stock.title')"
                      :msg="$t('stocks.has_treasury_stock.tip')" />
                    <h6>{{ item.has_treasury_stock === true ? 'Yes' : 'No' }}</h6>
                  </div>
                </div>
                <div class="row two-col">
                  <div class="col-xs-12 col-md-6">
                    <tooltip :isText="$t('stocks.cash_per_shares.title')" :msg="$t('stocks.cash_per_shares.tip')" />
                    <h6>{{ item.v_cash_per_shares.current_value | numberDisplay }} <span class="currencyFlag">{{
                        $t('currency') }}</span></h6>
                    <change-status v-if="item.v_cash_per_shares" :status="item.v_cash_per_shares" :unit="4" />
                  </div>
                </div>
                <div class="row two-col">
                  <div class="col-xs-6 col-md-6">
                    <small class="text-muted">{{ $t('dividend.title') }}</small>
                    <h6>{{ item.dividend | percentDisplay() }}</h6>
                  </div>
                  <div class="col-xs-6 col-md-6">
                    <small class="text-muted">{{ $t('dividend.average_title') }}</small>
                    <h6>{{ item.avgDividend | percentDisplay() }}</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-8 col-md-7" v-if="item.code">
          <!-- DCA -->
          <div class="card">
            <div class="tab-content">
              <div class="card-body">
                <h5 class="text-themecolor cursor-pointer m-0" @click="() => collapseSidebar('toggleDCA')">
                  <i class="fas fa-chess-queen"></i>
                  <span> {{ $t('stocks.dca.title') }}</span>
                  <span style="position: absolute; right: 30px; text-align: right; color: var(--green)">{{
                    dcaChart.chartOptions.title.text }}</span>
                  <a class="nav-link waves-effect waves-dark toggle-tab" data-toggle="dropdown">
                    <span>
                      <i class="fa" :class="{
                        'fa-angle-up': !this.form.toggleDCA,
                        'fa-angle-down': this.form.toggleDCA
                      }"></i>
                    </span>
                  </a>
                </h5>
                <div v-if="!this.form.toggleDCA" class="mt-2">
                  <loading-container :loading="evaluationForm.dcaCodeLoading">
                    <span class="cursor-pointer p-right" @click="() => collapseSidebar('dcaTips')">
                      {{ $t('stocks.dca.read_more_tips') }}
                      <i class="ml-1 fa fa fa-lightbulb text-warning"></i>
                    </span>
                    <apexchart height="600px" :options="dcaChart.chartOptions" :series="dcaChart.series">
                    </apexchart>
                  </loading-container>
                </div>
              </div>
            </div>
          </div>

          <!-- MOS -->
          <div class="card">
            <div class="tab-content">
              <div class="card-body">
                <h5 class="text-themecolor cursor-pointer m-0" @click="() => collapseSidebar('toggleMethods')">
                  <i class="far fa-life-ring"></i>
                  <span> {{ $t('stocks.valuation.title') }}</span>
                  <a class="nav-link waves-effect waves-dark toggle-tab" data-toggle="dropdown">
                    <span>
                      <i class="fa" :class="{
                        'fa-angle-up': !this.form.toggleMethods,
                        'fa-angle-down': this.form.toggleMethods
                      }"></i>
                    </span>
                  </a>
                </h5>
                <div v-if="!this.form.toggleMethods" class="mt-2">
                  <ul class="nav nav-tabs customtab" role="tablist">
                    <li class="nav-item">
                      <a class="nav-link cursor-pointer" :class="{ active: this.selectedTab === 'general' }"
                        @click="() => onSelectedTab('general')">
                        <i class="fas fa-adjust"></i>
                        <span class="hidden-sm-up"> {{ $t('stocks.general.title') }}</span>
                        <span class="hidden-xs-down"> {{ $t('stocks.general.title') }}</span>
                      </a>
                    </li>
                    <li class="nav-item">
                      <a class="nav-link cursor-pointer" :class="{ active: this.selectedTab === '10x' }"
                        @click="() => onSelectedTab('10x')">
                        <i class="fas fa-hand-holding-usd"></i>
                        <span class="hidden-sm-up"> {{ $t('stocks.ten_x_income.title') }}</span>
                        <span class="hidden-xs-down"> {{ $t('stocks.ten_x_income.title') }}</span>
                      </a>
                    </li>
                    <li class="nav-item">
                      <a class="nav-link cursor-pointer" :class="{ active: this.selectedTab === 'dcf' }"
                        @click="() => onSelectedTab('dcf')">
                        <i class="fas fa-tag"></i>
                        <span class="hidden-sm-up"> {{ $t('stocks.dcf.title') }}</span>
                        <span class="hidden-xs-down"> {{ $t('stocks.dcf.title') }}</span>
                      </a>
                    </li>
                  </ul>
                  <div class="tab-content">
                    <div class="tab-pane" :class="{ active: this.selectedTab === 'general' }">
                      <div class="card-body">
                        <general />
                      </div>
                    </div>
                    <div class="tab-pane" :class="{ active: this.selectedTab === '10x' }">
                      <div class="card-body">
                        <e10xIncome />
                      </div>
                    </div>
                    <div class="tab-pane" :class="{ active: this.selectedTab === 'dcf' }">
                      <div class="card-body">
                        <dcf />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Column -->
        <div class="col-md-12" v-if="!item.code">
          <div class="card">
            <div class="card-body">
              {{ form.stockOOLicense ? 'Access Revoked: Out of License for Stock Search. Contact admin to support!' :
              'Please input your search or your bussiness not found!' }}
            </div>
          </div>
        </div>
      </div>
      <div v-else class="m-t-20 m-b-20 m-l-10">
        This stock has not been analyzed yet. Click
        <span @click="() => submitRequest(defaultCode)">
          <i class="fas fa-file-medical text-blue medium-icon"></i>
        </span> to request an analysis,
        or click
        <span>
          <i class="fas fa-eye text-blue cursor-pointer medium-icon" @click="() => onAddToWatchlist(defaultCode)"></i>
        </span>
        to add to your watchlist.
      </div>
    </loading-container>

    <!-- MODAL DCA TIPS -->
    <modal :show="this.form.dcaTips" :close="() => setField({ key: 'dcaTips', value: false })"
      :title="$t('stocks.dca.tipsCode', {code: this.item.code})" class="dca-tips">
      <div>
        <table class="table table-bordered tips-table">
          <thead>
            <tr vertical-align="baseline">
              <th style="vertical-align: top">{{ $t(`stocks.dca.dcaIndex`) }}</th>
              <th style="vertical-align: top">
                <div><i class="fa fa-solid fa-circle text-warning"></i> {{ $t(`stocks.dca.good_level`) }}</div>
                <div><i class="fa fa-solid fa-circle text-success"></i> {{ $t(`stocks.dca.great_level`) }}</div>
                <div><i class="fa fa-solid fa-circle text-purple"></i> {{ $t(`stocks.dca.own`) }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in dcaChart.tips" :key="index">
              <td>{{ $t(`stocks.${dcaChart.fields[index]}.title`) }}</td>
              <td>
                <div v-html="displayTips($t(`stocks.${dcaChart.fields[index]}.tip`,
                  dcaChart.fields[index] === 'gross_profit_margin'
                  ? {beforeVal: 100 - item.good_level, value: item.good_level}
                  : {value: item.good_level}), 'text-warning')">
                </div>
                <div v-html="displayTips($t(`stocks.${dcaChart.fields[index]}.tip`,
                  dcaChart.fields[index] === 'gross_profit_margin'
                  ? {beforeVal: 100 - item.great_level, value: item.great_level}
                  : {value: item.great_level}), 'text-success')">
                </div>
                <div v-html="displayTips($t(`stocks.${dcaChart.fields[index]}.tip`,
                  dcaChart.fields[index] === 'gross_profit_margin'
                  ? {beforeVal: 100 - item.own, value: item.own}
                  : {value: item.own}), 'text-purple')">
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div slot="footer">
        <div class="flex-group">
          <button type="button" class="btn btn-secondary flex-item"
            @click="() => setField({ key: 'dcaTips', value: false })">
            {{ $t('common.close') }}
          </button>
        </div>
      </div>

    </modal>

    <!-- MODAL SAME SECTOR -->
    <modal :show="this.form.sameIndustry" :close="() => setField({ key: 'sameIndustry', value: false })"
      :title="$t('stocks.sameIndustry', {code: this.item.code})" class="dca-tips">
      <div>
        <loading-container :loading="form.stockIndustryLoading">
          <table class="table table-bordered compare-stock-table">
            <thead>
              <tr vertical-align="baseline">
                <th style="vertical-align: top">{{ $t(`stocks.information`) }}</th>
                <th class="th-sort"
                  :class="(sort.field === 'roe' && sort.order) ? (sort.order === 'desc' ? 'descending' : 'ascending') : ''"
                  @click="onSortField('roe')">
                  {{ $t('stocks.roe') }}
                </th>
                <th class="th-sort"
                  :class="(sort.field === 'pe' && sort.order) ? (sort.order === 'desc' ? 'descending' : 'ascending') : ''"
                  @click="onSortField('pe')">
                  {{ $t('stocks.current_pe') }}
                </th>
                <th class="th-sort"
                  :class="(sort.field === 'dividend' && sort.order) ? (sort.order === 'desc' ? 'descending' : 'ascending') : ''"
                  @click="onSortField('dividend')">
                  {{ $t('dividend.title') }}
                </th>
                <th class="th-sort"
                  :class="(sort.field === 'mos' && sort.order) ? (sort.order === 'desc' ? 'descending' : 'ascending') : ''"
                  @click="onSortField('mos')">
                  {{ $t('stocks.mos.title') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in industry" :key="index" :class="index === 0 ? 'bg-gray' : ''">
                <td :class="index === 0 ? 'font-bold' : ''">
                  <div>
                    {{ $t(`stocks.code`) }}:
                    <span class="cursor-pointer text-info" @click="() => linkToStockInfo(item.code)">
                      <span class="mytooltip tooltip-effect-5">
                        {{ item.code }}
                        <span class="tooltip-content clearfix">
                          <span class="tooltip-text p-t-10">{{ item.name }}</span>
                        </span>
                      </span>
                    </span>
                  </div>
                  <div>
                    {{ $t('stocks.debt_to_equity') }}:
                    <span class="text-info" :class="{
                      'text-purple font-bold': item.mvp.debt_to_equity
                      }">
                      {{ item.debt_to_equity | numberDisplayFloat(2) }}
                      <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                        <small>#{{ item.rank_no.debt_to_equity }}</small>
                        <span class="tooltip-content clearfix">
                          <span class="tooltip-text p-t-10">Top {{ item.rank_no.debt_to_equity }} {{
                            $t('stocks.debt_to_equity') }}</span>
                        </span>
                      </span>
                    </span>
                  </div>
                  <div>
                    {{ $t('stocks.current_pb') }}:
                    <span class="text-info" :class="{
                      'text-purple font-bold': item.mvp.pb
                      }">
                      {{ item.pb | numberDisplayFloat(3) }}
                      <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                        <small>#{{ item.rank_no.pb }}</small>
                        <span class="tooltip-content clearfix">
                          <span class="tooltip-text p-t-10">Top {{ item.rank_no.pb }} {{ $t('stocks.current_pb')
                            }}</span>
                        </span>
                      </span>
                    </span>
                  </div>

                  <div>
                    {{ $t('stocks.ps') }}:
                    <span class="text-info" :class="{
                      'text-purple font-bold': item.mvp.ps
                      }">
                      {{ item.ps | numberDisplayFloat(3) }}
                      <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                        <small>#{{ item.rank_no.ps }}</small>
                        <span class="tooltip-content clearfix">
                          <span class="tooltip-text p-t-10">Top {{ item.rank_no.ps }} {{ $t('stocks.ps') }}</span>
                        </span>
                      </span>
                    </span>
                  </div>
                </td>
                <td :class="{
                  'font-bold': index === 0,
                  'text-purple font-bold': item.mvp.roe
                }">
                  {{ item.roe | numberDisplayFloat(3) }}
                  <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                    <small>#{{ item.rank_no.roe }}</small>
                    <span class="tooltip-content clearfix">
                      <span class="tooltip-text p-t-10">Top {{ item.rank_no.roe }} {{ $t('stocks.roe') }}</span>
                    </span>
                  </span>
                </td>
                <td :class="{
                  'font-bold': index === 0,
                  'text-purple font-bold': item.mvp.pe
                }">
                  {{ item.pe | numberDisplayFloat(3) }}
                  <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                    <small>#{{ item.rank_no.pe }}</small>
                    <span class="tooltip-content clearfix">
                      <span class="tooltip-text p-t-10">Top {{ item.rank_no.pe }} {{ $t('stocks.current_pe') }}</span>
                    </span>
                  </span>
                </td>
                <td :class="{
                  'font-bold': index === 0,
                  'text-purple font-bold': item.mvp.dividend
                }">
                  {{ item.dividend | percentDisplay() }}
                  <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                    <small>#{{ item.rank_no.dividend }}</small>
                    <span class="tooltip-content clearfix">
                      <span class="tooltip-text p-t-10">Top {{ item.rank_no.dividend }} {{ $t('dividend.title')
                        }}</span>
                    </span>
                  </span>
                </td>

                <td :class="{
                  'font-bold': index === 0,
                  'text-purple font-bold': item.mvp.mos
                }">
                  {{ valueFilter(item.mos) }}
                  <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                    <small>#{{ item.rank_no.mos }}</small>
                    <span class="tooltip-content clearfix">
                      <span class="tooltip-text p-t-10">Top {{ item.rank_no.mos }} {{ $t('stocks.mos.title') }}</span>
                    </span>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </loading-container>
      </div>
      <div slot="footer">
        <div class="flex-group">
          <button type="button" class="btn btn-secondary flex-item"
            @click="() => setField({ key: 'sameIndustry', value: false })">
            {{ $t('common.close') }}
          </button>
        </div>
      </div>

    </modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Breadcrumb from "@/components/Common/Breadcrumb";
import ChangeStatus from "@/components/Common/ChangeStatus";
import LoadingContainer from "@/components/Misc/LoadingContainer";
import Tooltip from "@/components/Common/Tooltip";
import AddWatchList from "@/components/Common/AddWatchList";
import Modal from "@/components/Misc/Modal";
import i18n from '../../../i18n';
import General from '../Evaluation/General';
import Dcf from '../Evaluation/Dcf';
import E10xIncome from '../Evaluation/10xIncome';
import IndexStatus from '@/components/Common/IndexStatus';
import common from "@/libs/common";

export default {
  name: "EvaluationMain",
  components: {
    Breadcrumb,
    ChangeStatus,
    LoadingContainer,
    Tooltip,
    AddWatchList,
    Modal,
    General,
    Dcf,
    E10xIncome,
    IndexStatus
  },
  beforeMount() {
    const code = this.defaultCode;
    if (!this.item.code || this.item.code != code) {
      this.getStockInfo(code);
      this.getDCACode(code);
    }
  },
  data() {
    return {
      selectedTab: "general"
    };
  },
  computed: {
    ...mapGetters({
      item: "stock/item",
      user: "auth/user",
      form: "stock/form",
      sort: "stock/sort",
      evaluationForm: "evaluation/form",
      defaultCode: "configuration/defaultCode",
      dcaChart: "evaluation/dcaChart",
      industry: "stock/industry",
      defaultLocale: "configuration/defaultLocale"
    }),
    currentPage() {
      return i18n.t(`stocks.information`);
    },
    currentUser() {
      return this.user || {};
    },
  },
  methods: {
    ...mapActions({
      getStockInfo: "stock/getStockInfo",
      getDCACode: "evaluation/getDCACode",
      setField: "stock/setField",
      setSortField: "stock/setSortField",
      setDefaultCode: "configuration/setDefaultCode",
      getStockSameIndustryV2: "stock/getStockSameIndustryV2",
      onAddToWatchlist: "stock/onAddToWatchlist",
      requestNewStock: "stock/requestNewStock",
    }),
    submitRequest(code) {
      if (code) {
        this.requestNewStock(code);
      }
    },
    onSortField(sortField) {
      if(sortField === this.sort.field) {
        this.setSortField({ key: 'order', value: this.sort.order === 'asc' ? 'desc' : 'asc' });
      }
      this.setSortField({ key: 'field', value: sortField });
      this.getStockSameIndustryV2(this.item.code);
    },
    actionGetSameIndustry(key) {
      this.collapseSidebar(key);
      this.getStockSameIndustryV2(this.item.code);
    },
    collapseSidebar(key) {
      this.setField({ key: key, value: !this.form[key] });
    },
    formattedDescription() {
      if (this.form.toggleDes) {
        return this.item.desc;
      }
      return this.item.desc.length > 100 ? `${this.item.desc.slice(0, 100).trim()}...` : 'N/A';
    },
    displayTips(data, type) {
      const text = `<span class="${type}">`;
      const split = data.split('|');
      const list = [];
      for (let i = 0; i < split.length; i++) {
        if (i % 2 === 1) {
          list.push(`${text}${split[i]}</span>`);
        } else {
          list.push(`${split[i]}`);
        }
      } return list.join(' ');
    },
    linkToStockInfo(code) {
      this.setDefaultCode(code);
      this.collapseSidebar('toggleInfo')
      let routeData = this.$router.resolve({name: 'stockInfo', params: { codeId: code } });
      window.open(routeData.href, '_blank');
    },
    onSelectedTab(key) {
      this.selectedTab = key;
    },
    valueFilter(value, isDca) { 
      if (isDca) {
        return _.round(value, 1);  
      }
      return _.round(value * 100, 1) + '%';
    },
  },
  filters: {
    hasRole(name, user) {
      return common.checkRole(name, user);
    }
  }
};
</script>
<style scoped>
.toggle-tab {
  position: absolute;
  right: 0;
  top: 13px;
}
.toggle-tab.toggle-tab-method {
  top: 8px;
}
.mytooltip {
  z-index: 10;
}
.tips-table th {
  font-weight: 600;
  font-size: 13px;
}
.tips-table td {
  font-size: 13px;
}
td small {
  vertical-align: super;
}
.customtab {
  overflow-x: auto;
  overflow-y: hidden;
  -ms-flex-wrap: nowrap;
  flex-wrap: nowrap;
  display: -webkit-box;
}
.bg-gray {
  background-color: #ddd;
}
.last-update {
  font-style: italic;
  font-size: 12px;
}
.economic-data {
  font-size: 12px;
}

.economic-data .economic-data-detail {
  font-weight: 600;
}
.th-sort {
  position: relative;
  vertical-align: top;
  cursor: pointer;
}

.descending:after {
  content: "\2193";
}
.ascending:after {
  content: "\2191";
}

.ascending:after,
.descending:after {
  display: inline-block;
}

@media only screen and (max-width: 768px) {
  table.compare-stock-table th,
  table.compare-stock-table td {
    font-size: 12px;
    padding: 10px;
  }
}

@media only screen and (max-width: 500px) {
  table.compare-stock-table th,
  table.compare-stock-table td {
    font-size: 10px;
    padding: 2px;
  }
}
</style>